import { css } from '@emotion/react';
import { DialogInteractionType, FixedPosition } from '../../types/app-config';
import { RESIZE_HANDLER_SIZE } from '../utils/resize-utils';
function convertValue(value) {
    if (typeof value === 'object') {
        return `${value.distance}${value.unit}`;
    }
    return value;
}
function getDialogBackgroundSize(dialogBackground) {
    let size = 'unset';
    if (dialogBackground && dialogBackground.image) {
        switch (dialogBackground.fillType) {
            case 'fit':
                size = 'contain';
                break;
            case 'fill':
                size = 'cover';
                break;
            case 'stretch':
                size = '100% 100%';
                break;
            default:
                size = 'auto';
                break;
        }
        return size;
    }
    return size;
}
// Get bg, border, shadow styles
function getDialogStyles(dialogJson) {
    var _a;
    const { dialogBackground, border, borderRadius, boxShadow } = dialogJson;
    let borderRadiusTopStyle = 'unset';
    let borderRadiusBottomStyle = 'unset';
    if (borderRadius) {
        const lt = borderRadius.number[0];
        const rt = borderRadius.number[1] !== undefined ? borderRadius.number[1] : lt;
        const rb = borderRadius.number[2] !== undefined ? borderRadius.number[2] : lt;
        const lb = borderRadius.number[3] !== undefined ? borderRadius.number[3] : lt;
        borderRadiusTopStyle = `${lt}${borderRadius.unit} ${rt}${borderRadius.unit}`;
        borderRadiusBottomStyle = `${rb}${borderRadius.unit} ${lb}${borderRadius.unit}`;
    }
    return {
        backgroundColor: dialogBackground && dialogBackground.color ? dialogBackground.color : 'transparent',
        backgroundImage: ((_a = dialogBackground === null || dialogBackground === void 0 ? void 0 : dialogBackground.image) === null || _a === void 0 ? void 0 : _a.url) ? `url(${dialogBackground.image.url})` : 'unset',
        backgroundRepeat: dialogBackground && dialogBackground.image && dialogBackground.fillType === 'tile' ? 'repeat' : 'no-repeat',
        backgroundSize: getDialogBackgroundSize(dialogBackground),
        borderStyle: (border && border.color && border.width) ? `${convertValue(border.width)} ${border.type} ${border.color}` : 'none',
        borderRadiusTopStyle: borderRadiusTopStyle,
        borderRadiusBottomStyle: borderRadiusBottomStyle,
        borderRadiusStyle: borderRadius ? `${borderRadiusTopStyle} ${borderRadiusBottomStyle}` : 'unset',
        boxShadowStyle: boxShadow && boxShadow.color
            ? `${convertValue(boxShadow.offsetX)} ${convertValue(boxShadow.offsetY)}
      ${convertValue(boxShadow.blur)} ${convertValue(boxShadow.spread)} ${boxShadow.color}`
            : 'none'
    };
}
const BOX_SHADOW_LIST = { none: 'shadow-none', small: 'shadow-sm', default: 'shadow', large: 'shadow-lg' };
/**
 * Get boxShadow prop from quick style
 */
function getBoxShadowByQuickStyle(quickStyle) {
    if (quickStyle === BOX_SHADOW_LIST.none) {
        return null;
    }
    const boxShadow = {
        offsetX: '0px',
        offsetY: '0px',
        blur: '0px',
        spread: '0px',
        color: ''
    };
    switch (quickStyle) {
        case BOX_SHADOW_LIST.small:
            boxShadow.blur = '6px';
            boxShadow.spread = '2px';
            boxShadow.color = 'rgba(0,0,0,0.15)';
            break;
        case BOX_SHADOW_LIST.default:
            boxShadow.blur = '10px';
            boxShadow.spread = '4px';
            boxShadow.color = 'rgba(0,0,0,0.2)';
            break;
        case BOX_SHADOW_LIST.large:
            boxShadow.blur = '20px';
            boxShadow.spread = '6px';
            boxShadow.color = 'rgba(39,60,70,0.2)';
            break;
        default:
            break;
    }
    return boxShadow;
}
function getDialogJsonWithQuickBoxShadow(dialogJson) {
    var _a;
    if ((_a = dialogJson.boxShadow) === null || _a === void 0 ? void 0 : _a.presetStyle) {
        dialogJson = dialogJson.set('boxShadow', getBoxShadowByQuickStyle(dialogJson.boxShadow.presetStyle));
    }
    return dialogJson;
}
// Styles for fixed dialog (global)
export function getGlobalStylesForFixed(dialogJson, isFixedDlgOpen, browserHeight, dialogSizeMode) {
    dialogJson = getDialogJsonWithQuickBoxShadow(dialogJson);
    const { borderRadius, interactionType } = dialogJson;
    const styles = getDialogStyles(dialogJson);
    const overlayColor = dialogJson.overlayColor || (dialogJson.isOpenerDlg ? 'var(--ref-palette-neutral-1200)' : 'transparent');
    const dlgWrapId = `app-dialog-wrap-${dialogJson.id}`;
    const dialogContentBorderRadius = borderRadius && interactionType ? `${styles.borderRadiusTopStyle} 0 0` : styles.borderRadiusStyle;
    const dialogFooterBorderRadius = borderRadius ? `0 0 ${styles.borderRadiusBottomStyle}` : 'unset';
    return css `
    body {

      @keyframes aBlink {
        from { background-color: var(--sys-color-error-dark); }
        50% { background-color: var(--sys-color-error-dark); }
        51%{background-color: transparent;}
        to { background-color: transparent; }
      }

      .app-dialog-wrap{

        &.${dlgWrapId}{
          display: ${isFixedDlgOpen ? 'inherit' : 'none'};
          &.no-backdrop {
            pointer-events: none;
            .modal-content {
              pointer-events: auto;
            }
          }
          .app-dialog {
            margin: 0!important;
            max-width: 100vw;
            max-height: 100vh;

            .app-dialog-content {
              border: none;
              box-shadow: unset;
              background-color: transparent;
              border-radius:  ${styles.borderRadiusStyle};

              &.overflow-visible{
                overflow: visible;
              }

              .app-dialog-animation-content {
                flex-shrink: 0;
                border: ${styles.borderStyle};
                border-radius:  ${styles.borderRadiusStyle};
                box-shadow: ${styles.boxShadowStyle};
                background-color: ${styles.backgroundColor};
                background-image: ${styles.backgroundImage};
                background-position: center center;
                background-repeat: ${styles.backgroundRepeat};
                background-size: ${styles.backgroundSize};

                .dialog-content{
                  padding: 0;
                  border-radius:  ${dialogContentBorderRadius};
                }

                .dialog-footer{
                  overflow-x: auto;
                  border-radius:  ${dialogFooterBorderRadius};
                  user-select: none;
                  &.has-mask {
                    pointer-events: none;
                    user-select: none;
                    .btn {
                      pointer-events: none;
                    }
                  }

                  .cbx-animation{
                    margin-right: 0.5rem;
                    width: 16px;
                    height: 16px;
                    border: 2px solid var(--ref-palette-neutral-600);
                    animation: aBlink 500ms infinite;
                  }
                  .btn {
                    min-width: 6.25rem;
                  }
                }
              }
            }
          }
          .app-dialog-backdrop{
            background-color: ${overlayColor};
          }
        }
      }
    }
  `;
}
// Styles for fixed dialog
export function getStylesForFixed(dialogSizeMode, browserHeight, interactionType) {
    let contentHeight = null;
    // get pixel from percent for height
    if (dialogSizeMode.heightMode === 'CUSTOM') {
        const unit = dialogSizeMode.height.includes('%') ? '%' : 'px';
        const _height = unit === '%' ? browserHeight * parseFloat(dialogSizeMode.height.split('%')[0]) / 100 + 'px' : dialogSizeMode.height;
        if (parseFloat(_height) > browserHeight) {
            contentHeight = _height;
        }
    }
    if (interactionType) { // with footer
        const footerHeight = interactionType === DialogInteractionType.ButtonBlock ? 95 : (interactionType === DialogInteractionType.ButtonInline ? 64 : 59);
        contentHeight = `calc(100% - ${footerHeight}px)`;
    }
    return css `
    &.app-dialog{
      .app-dialog-content { /** modal-content */
        max-width: 100vw;
        max-height: 100vh;
        ${convertToStyle(dialogSizeMode)}
        height: ${dialogSizeMode.heightMode === 'CUSTOM' && dialogSizeMode.height};
        /* transition: height 0.3s ease-in-out; */
        transform: ${panelTransformStyle(dialogSizeMode)};
        .app-dialog-resize-container {
          width: 100%;
          height: 100%;

          .app-dialog-animation-content {
            width: 100%;
            height: 100%;
            overflow-y: auto;
            .modal-body { /** dialog-content, it might have scrolling-bar in height */
              width: 100%;
              height: 100%;
              height: ${contentHeight};
            }
          }
        }
      }
    }
  `;
}
// Styles for anchored dialog
export function getStylesForAnchored(dialogJson, dialogSizeMode) {
    var _a;
    dialogJson = getDialogJsonWithQuickBoxShadow(dialogJson);
    const styles = getDialogStyles(dialogJson);
    const backgroundColor = styles.backgroundColor === 'transparent' ? 'var(--ref-palette-neutral-200)' : styles.backgroundColor;
    /**
     * Use shadow styles of Popper by default.
     * Show custom shadow if it's set. And at this case, show extra outline style when no border is set.
     *  */
    const hasBoxShadow = (_a = dialogJson.boxShadow) === null || _a === void 0 ? void 0 : _a.color;
    const hasBorder = dialogJson.border && dialogJson.border.width && dialogJson.border.color;
    const defaultBoxShadowStyle = '0 0 10px 1px var(--ref-palette-neutral-600)';
    const defaultOutlineStyle = '1px solid var(--ref-palette-neutral-600)';
    const MARGIN = '12px';
    let customHeight = dialogSizeMode.height;
    let arrowBorderColor = null;
    let arrowBorderOffset = null;
    if (dialogJson.border && dialogJson.border.width && dialogJson.border.color) {
        arrowBorderColor = dialogJson.border.color;
        arrowBorderOffset = dialogJson.border.width;
        customHeight = `calc(${customHeight} - ${arrowBorderOffset} - ${arrowBorderOffset})`;
    }
    const style = css `
    &.app-dialog{
      /* margin: 0.5rem !important; */
      margin: 0 !important;
      background-color: transparent;
      border: none;
      box-shadow: unset;

      &.dialog-animation-playing{
        .jimu-popper--arrow{
          visibility: hidden !important;
        }

        .popper-box{
          overflow: visible !important; /*for animation is playing*/
        }
      }
      &.dialog-animation-end{
        .app-dialog-animation-content {
          border-radius: ${styles.borderRadiusStyle};
        }

        &[data-popper-placement^="top"] .jimu-popper--arrow{
          &::before{
            border-top-color: ${arrowBorderColor};
          }
          &::after{
            border-top-color: ${backgroundColor};
            bottom: ${arrowBorderOffset};
          }
        }

        &[data-popper-placement^="bottom"] .jimu-popper--arrow{
          &::before{
            border-bottom-color: ${arrowBorderColor};
          }
          &::after{
            border-bottom-color: ${backgroundColor};
            top: ${arrowBorderOffset};
          }
        }

        &[data-popper-placement^="left"] .jimu-popper--arrow{
          &::before{
            border-left-color: ${arrowBorderColor};
          }
          &::after{
            border-left-color: ${backgroundColor};
            right: ${arrowBorderOffset};
          }
        }

        &[data-popper-placement^="right"] .jimu-popper--arrow{
          &::before{
            border-right-color: ${arrowBorderColor};
          }
          &::after{
            border-right-color: ${backgroundColor};
            left: ${arrowBorderOffset};
          }
        }
      }

      .popper-box{
        max-width: calc(100vw - ${MARGIN});
        max-height: calc(100vh - ${MARGIN});
        width: ${dialogSizeMode.width};
        height: ${dialogSizeMode.height};
        border-radius:  ${styles.borderRadiusStyle};
      }

      .app-dialog-animation-content{
        height: 100%;
        box-shadow: ${hasBoxShadow ? styles.boxShadowStyle : defaultBoxShadowStyle};

        .app-dialog-container{
          overflow-y: auto;
          max-width: calc(100vw - ${MARGIN});
          max-height: calc(100vh - ${MARGIN});
          width: 100%;

          border: ${hasBorder ? styles.borderStyle : 'none'};
          outline: ${hasBoxShadow && !hasBorder ? defaultOutlineStyle : 'unset'};
          border-radius:  ${styles.borderRadiusStyle};

          .app-dialog-content{
            width: 100%;
            height: ${customHeight};
            border-radius:  ${styles.borderRadiusStyle};
            background-color: ${backgroundColor} ;
            background-image: ${styles.backgroundImage};
            background-position: center center;
            background-repeat: ${styles.backgroundRepeat};
            background-size: ${styles.backgroundSize};
          }
        }
      }

    }
  `;
    return style;
}
// Styles for resize handlers
export function getResizeStyle() {
    const length = 38;
    return css `
    .app-dialog-resize-handlers{
      .resize-handler{
        width: 100%;
        height: 100%;
        position: absolute;
        z-index: 1;
        .resize-handler-icon {
          position: absolute;
          background-color: var(--ref-palette-black);
          border: 1px solid var(--ref-palette-white);
        }
        &.dialog-resize-top, &.dialog-resize-bottom {
          height: ${RESIZE_HANDLER_SIZE};
          cursor: ns-resize;
          .resize-handler-icon{
            left: min(50%, 50vw);
            width: ${length}px;
            margin-left: ${-length / 2}px;
          }
        }
        &.dialog-resize-top{
          top: 0;
        }
        &.dialog-resize-bottom{
          bottom: 0;
        }
        &.dialog-resize-left, &.dialog-resize-right{
          width: ${RESIZE_HANDLER_SIZE};
          top: 0;
          cursor: ew-resize;
          .resize-handler-icon{
            top: 50%;
            height: ${length}px;
            margin-top: ${-length / 2}px;
          }
        }
        &.dialog-resize-left{
          left: 0;
        }
        &.dialog-resize-right{
          right: 0;
        }
      }
    }
  `;
}
function panelTransformStyle(dlgJson) {
    if (dlgJson == null) {
        return null;
    }
    const { position, offsetX = 0, offsetY = 0 } = dlgJson;
    let translate;
    if (dlgJson.widthMode !== 'STRETCH') {
        if (offsetX !== 0) {
            translate = `translateX(${offsetX}px)`;
        }
        if (position === FixedPosition.TopCenter || position === FixedPosition.MiddleCenter || position === FixedPosition.BottomCenter) {
            translate = translate != null ? `${translate} translateX(-50%)` : 'translateX(-50%)';
        }
    }
    if (dlgJson.heightMode !== 'STRETCH') {
        if (offsetY !== 0) {
            translate = translate != null ? `${translate} translateY(${offsetY}px)` : `translateY(${offsetY}px)`;
        }
        if (position === FixedPosition.MiddleLeft || position === FixedPosition.MiddleCenter || position === FixedPosition.MiddleRight) {
            translate = translate != null ? `${translate} translateY(-50%)` : 'translateY(-50%)';
        }
    }
    return translate;
}
function convertToStyle(dlgJson) {
    let posStyle;
    switch (dlgJson === null || dlgJson === void 0 ? void 0 : dlgJson.position) {
        case FixedPosition.TopLeft:
            posStyle = css `top: 0; left: 0; bottom: auto; right: auto;`;
            break;
        case FixedPosition.TopRight:
            posStyle = css `top: 0; left: auto; bottom: auto; right: 0;`;
            break;
        case FixedPosition.BottomLeft:
            posStyle = css `top: auto; left: 0; bottom: 0; right: auto;`;
            break;
        case FixedPosition.BottomRight:
            posStyle = css `top: auto; left: auto; bottom: 0; right: 0;`;
            break;
        case FixedPosition.TopCenter:
            posStyle = css `top: 0; left: 50%; bottom: auto; right: auto;`;
            break;
        case FixedPosition.BottomCenter:
            posStyle = css `top: auto; left: 50%; bottom: 0; right: auto;`;
            break;
        case FixedPosition.MiddleLeft:
            posStyle = css `top: 50%; left: 0; bottom: auto; right: auto;`;
            break;
        case FixedPosition.MiddleRight:
            posStyle = css `top: 50%; left: auto; bottom: auto; right: 0;`;
            break;
        case FixedPosition.MiddleCenter:
            posStyle = css `top: 50%; left: 50%; bottom: auto; right: auto;`;
            break;
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        default:
            posStyle = css `top: 0; left: 0; bottom: auto; right: auto;`;
            break;
    }
    let widthModeStyle;
    let heightModeStyle;
    if (dlgJson.widthMode === 'STRETCH') {
        widthModeStyle = css `width:auto;left:${dlgJson.left};right:${dlgJson.right};`;
    }
    if (dlgJson.heightMode === 'STRETCH') {
        heightModeStyle = css `height:auto;top:${dlgJson.top};bottom:${dlgJson.bottom};`;
    }
    return css `
    position: fixed;
    ${posStyle}
    width: ${dlgJson === null || dlgJson === void 0 ? void 0 : dlgJson.width};
    height: ${dlgJson === null || dlgJson === void 0 ? void 0 : dlgJson.height};
    ${widthModeStyle};
    ${heightModeStyle}
    overflow: visible;
    z-index: 2;
  `;
}
